function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Button } from "@progress/kendo-react-buttons";
import { PageHeader } from "../../components/analytics-kit";
import { TrimModal, ClipsModal, VideoEditModal, VideoResizeModal } from "./video-edit";
import { AssetPicker } from "./sequence-create";
import { ExportsPanel, latestBatch, latestDonePerFormat, exportName } from "./sequence-outputs";
import { previewLine, playAudio, stopAudio, playingKey } from "./voices";
import { SceneFrame, ExportStage } from "./sequence-stage";
import SequenceTimeline from "./sequence-timeline";
import { ScenePanel, SoundPanel, LookPanel } from "./sequence-panels";
import { SIZES, lenOf, segSeconds, isCard } from "./sequence-common";
import { hasBothApps, studioUrl } from "./images-api";
import * as api from "./sequences-api";

/*
 * Sequence editor (storyboard workspace), redesigned 2026-10-08 to PlunkDocs mockups/video-editors-redesign-2026-10-08.html
 * screens 3 to 5, in the image editor's style. Route: /Content/sequences/:id.
 *
 * Top bar: back to Assets, title (click to rename) + "purpose · N scenes · length", preview size switch (drives the
 * frame and the safe areas), Saved state, Rewrite the cut, Render N sizes (the sizes chosen in Look).
 * Stage: the selected scene in the chosen size; Play all steps through the scenes on their lengths (and plays each
 * line in the chosen voice through the preview endpoint when "With voice" is on). The real render adds motion and music.
 * Timeline (sequence-timeline.jsx): widths follow lengths, drag to reorder, drag the right edge to set a length.
 * Side tabs: Scene / Sound / Look (sequence-panels.jsx) and Exports (sequence-outputs.jsx).
 *
 * Kept from the storyboard: autosave PUT /sequences/{id}/segments 800 ms after the last edit with the full ordered
 * list; PUT /sequences/{id} for settings (600 ms debounce). A 409 `rendering` holds saves calmly until the batch
 * finishes (editing stays open; the pending save runs after). Polling only: GET renders/{renderId} every 10 s per
 * in-flight render; the localStorage batch key re-attaches a render that outlived the page. No credit amounts
 * anywhere; a 402 shows the insufficient-credits state with Buy credits.
 */

const toast = api.toast;
const SAVE_MS = 800;
const SETTINGS_MS = 600;
const TICK_MS = 80;
const totalOf = segs => (segs || []).reduce((t, s) => t + lenOf(s), 0);
class SequenceWorkspace extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      data: undefined,
      loadErr: "",
      format: "16:9",
      sel: null,
      saved: "",
      lock: false,
      busy: "",
      sideTab: "scene",
      credits: null,
      confirmCut: false,
      rewriting: null,
      addOpen: false,
      addPicks: [],
      changeOpen: false,
      changePick: [],
      titleEdit: null,
      hook: null,
      attachBusy: false,
      playing: false,
      playT: 0,
      voiceOn: true,
      watchId: null
    });
    _defineProperty(this, "_alive", true);
    _defineProperty(this, "_polls", {});
    _defineProperty(this, "_saveTimer", null);
    _defineProperty(this, "_settingsTimer", null);
    _defineProperty(this, "_pendingSave", false);
    _defineProperty(this, "_pendingSettings", {});
    _defineProperty(this, "_saving", false);
    _defineProperty(this, "_dirtyAt", 0);
    _defineProperty(this, "_addAssets", []);
    _defineProperty(this, "_changeAssets", []);
    _defineProperty(this, "_tick", null);
    _defineProperty(this, "_t0", 0);
    _defineProperty(this, "_lastIdx", -1);
    _defineProperty(this, "_pv", {});
    _defineProperty(this, "_pvWarned", false);
    _defineProperty(this, "_voiceKey", null);
    _defineProperty(this, "onKey", e => {
      if (e.key === "Escape") {
        if (this.state.addOpen) this.setState({
          addOpen: false
        });else if (this.state.changeOpen) this.setState({
          changeOpen: false
        });else if (this.state.confirmCut) this.setState({
          confirmCut: false
        });
      }
    });
    /** Edits held back by a running render would be lost on a hard leave (reload, close): let the browser ask. */
    _defineProperty(this, "onLeave", e => {
      if (this.state.lock && (this._dirtyAt || this._pendingSave || Object.keys(this._pendingSettings).length)) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
      return undefined;
    });
    /* ---------- load and polling ---------- */
    _defineProperty(this, "load", async () => {
      const id = this.seqId();
      if (!id) {
        this.setState({
          data: null,
          loadErr: "No sequence id."
        });
        return;
      }
      try {
        const d = await api.getSequence(id);
        if (!d) throw new Error("Could not open this sequence.");
        if (!d.renders.length) d.renders = await api.listRenders(id);
        if (!this._alive) return;
        const fmt = d.sequence.formats.includes(this.state.format) ? this.state.format : d.sequence.formats[0] || "16:9";
        const sel = this.state.sel && d.segments.some(x => x._key === this.state.sel) ? this.state.sel : d.segments[0] ? d.segments[0]._key : null;
        this.setState({
          data: d,
          format: fmt,
          sel,
          saved: d.sequence.scriptVersion ? `Saved · v${d.sequence.scriptVersion}` : "Saved",
          lock: d.sequence.status === "rendering"
        }, this.attachBatch);
      } catch (e) {
        const info = api.errInfo(e);
        if (this._alive) this.setState({
          data: null,
          loadErr: info.soft ? "soft" : info.status === 404 ? "This sequence was not found (it may have been deleted)." : info.message || "Could not open this sequence."
        });
      }
    });
    /** Poll every render that is queued or rendering, plus the ids in the localStorage batch key. */
    _defineProperty(this, "attachBatch", () => {
      const {
        data
      } = this.state;
      if (!data) return;
      const ids = new Set(data.renders.filter(api.inFlight).map(r => r.id));
      const b = api.loadBatch(this.seqId());
      if (b) b.renderIds.forEach(x => {
        const r = data.renders.find(y => y.id === x);
        if (!r || api.inFlight(r)) ids.add(x);
      });
      if (!ids.size) {
        api.clearBatch(this.seqId());
        return;
      }
      this.setState({
        lock: true
      });
      ids.forEach(rid => this.pollOne(rid, true));
    });
    _defineProperty(this, "pollOne", (rid, now) => {
      clearTimeout(this._polls[rid]);
      this._polls[rid] = setTimeout(async () => {
        if (!this._alive) return;
        let r = null;
        let gone = false;
        try {
          r = await api.getRender(this.seqId(), rid);
        } catch (e) {
          const info = api.errInfo(e);
          if (info.status === 404) gone = true;
        }
        if (!this._alive) return;
        if (r) this.setState(st => ({
          data: {
            ...st.data,
            renders: st.data.renders.some(x => x.id === r.id) ? st.data.renders.map(x => x.id === r.id ? r : x) : [...st.data.renders, r]
          }
        }));
        if (gone || r && !api.inFlight(r)) {
          delete this._polls[rid];
          if (r && r.status === "done") toast(`${exportName(r.format)} is ready.`);
          if (r && r.status === "failed") toast(`${exportName(r.format)} did not finish. See Exports.`);
          if (!Object.keys(this._polls).length) this.batchDone();
        } else this.pollOne(rid);
      }, now ? 0 : api.POLL_MS);
    });
    _defineProperty(this, "batchDone", async () => {
      api.clearBatch(this.seqId());
      try {
        const d = await api.getSequence(this.seqId());
        if (d && this._alive) this.setState(st => ({
          data: {
            ...st.data,
            sequence: {
              ...d.sequence,
              scriptVersion: Math.max(d.sequence.scriptVersion, st.data.sequence.scriptVersion)
            },
            renders: d.renders.length ? d.renders : st.data.renders
          }
        }));
      } catch (e) {/* keep what we have */}
      if (!this._alive) return;
      this.setState({
        lock: false
      });
      if (this._pendingSave) {
        this._pendingSave = false;
        this.saveSegments();
      }
      if (Object.keys(this._pendingSettings).length) this.flushSettings();
    });
    _defineProperty(this, "patchSeg", (key, patch) => this.setSegs(this.segs().map(s => s._key === key ? {
      ...s,
      ...patch
    } : s)));
    _defineProperty(this, "removeSeg", key => {
      const segs = this.segs();
      if (segs.length <= 1) {
        toast("Keep at least one scene.");
        return;
      }
      const i = segs.findIndex(s => s._key === key);
      const next = segs.filter(s => s._key !== key);
      this.setSegs(next);
      if (this.state.sel === key) this.setState({
        sel: (next[Math.min(i, next.length - 1)] || {})._key || null
      });
    });
    _defineProperty(this, "moveSeg", (from, to) => {
      const segs = [...this.segs()];
      if (to < 0 || to >= segs.length || from === to) return;
      const [m] = segs.splice(from, 1);
      segs.splice(to, 0, m);
      this.setSegs(segs);
    });
    _defineProperty(this, "duplicateSeg", key => {
      const segs = this.segs();
      const i = segs.findIndex(s => s._key === key);
      if (i < 0) return;
      if (segs.length >= api.MAX_PICKS) {
        toast(`Up to ${api.MAX_PICKS} scenes in one sequence.`);
        return;
      }
      const copy = api.normSegment({
        ...segs[i],
        id: null,
        _key: null,
        warnings: []
      }, segs.length);
      const next = [...segs.slice(0, i + 1), copy, ...segs.slice(i + 1)];
      this.setSegs(next);
      this.setState({
        sel: copy._key
      });
    });
    /** "Into the next scene" on scene i writes the transition into scene i + 1 (the segment's transitionIn). */
    _defineProperty(this, "patchNext", patch => {
      const segs = this.segs();
      const i = segs.findIndex(s => s._key === this.state.sel);
      if (i >= 0 && segs[i + 1]) this.patchSeg(segs[i + 1]._key, patch);
    });
    _defineProperty(this, "setLength", (key, sec) => this.patchSeg(key, {
      durationSeconds: sec
    }));
    _defineProperty(this, "addCard", style => {
      const seq = this.state.data.sequence;
      const seg = api.normSegment({
        kind: "card",
        card: {
          headline: style === "title" ? seq.title : "Learn more",
          sub: style === "title" ? "" : seq.brand && seq.brand.website || "",
          style
        },
        transitionIn: "crossfade",
        durationSeconds: 3
      }, this.segs().length);
      const segs = style === "title" ? [seg, ...this.segs()] : [...this.segs(), seg];
      this.setSegs(segs);
      this.setState({
        sel: seg._key,
        sideTab: "scene"
      });
    });
    _defineProperty(this, "onAdd", what => {
      if (what === "assets") this.setState({
        addOpen: true,
        addPicks: []
      });else this.addCard(what);
    });
    _defineProperty(this, "addFromAssets", () => {
      const ids = this.state.addPicks;
      if (!ids.length) {
        this.setState({
          addOpen: false
        });
        return;
      }
      const byId = {};
      this._addAssets.forEach(a => {
        byId[a.id] = a;
      });
      const added = ids.map((id, k) => api.normSegment(this.segFromAsset(byId[id] || {
        id
      }, k), this.segs().length + k));
      this.setSegs([...this.segs(), ...added]);
      this.setState({
        addOpen: false,
        addPicks: [],
        sel: added[0]._key,
        sideTab: "scene"
      });
      toast(`${added.length} scene${added.length === 1 ? "" : "s"} added. The voiceover is empty until you write it or rewrite the cut.`);
    });
    /** Change: the selected scene uses another asset; its words, transition and length stay. */
    _defineProperty(this, "changeAsset", () => {
      const id = this.state.changePick[0];
      const key = this.state.sel;
      if (!id || !key) {
        this.setState({
          changeOpen: false
        });
        return;
      }
      const a = this._changeAssets.find(x => x.id === id) || {
        id
      };
      this.patchSeg(key, {
        ...this.segFromAsset(a),
        trimStart: 0,
        trimEnd: null,
        warnings: []
      });
      this.setState({
        changeOpen: false,
        changePick: []
      });
    });
    _defineProperty(this, "saveSegments", async unmounting => {
      // while a render runs the server answers 409: hold the save (a leaving page still tries once, the render may be done)
      if (this.state.lock && unmounting !== true) {
        this._pendingSave = true;
        this.setState({
          saved: "Rendering: saved when it finishes"
        });
        return;
      }
      if (this._saving) {
        this._pendingSave = true;
        return;
      }
      const sent = this.segs();
      const sentAt = Date.now();
      this._saving = true;
      if (unmounting !== true) this.setState({
        saved: "Saving…"
      });
      try {
        const r = await api.putSegments(this.seqId(), sent);
        if (!this._alive) return;
        this._dirtyAt = this._dirtyAt > sentAt ? this._dirtyAt : 0;
        this.setState(st => {
          const local = st.data.segments;
          let segments;
          if (r.segments.length === local.length) {
            // positional merge: the server owns ids, positions, media and the computed fields; local text wins when it changed since the save went out
            segments = local.map((s, i) => {
              const sv = r.segments[i];
              const edited = this._dirtyAt > sentAt;
              return edited ? {
                ...sv,
                ...s,
                id: sv.id || s.id,
                mediaUrl: sv.mediaUrl || s.mediaUrl,
                thumbnailUrl: sv.thumbnailUrl || s.thumbnailUrl,
                title: sv.title || s.title,
                narrationSeconds: sv.narrationSeconds,
                estimatedSeconds: sv.estimatedSeconds,
                warnings: sv.warnings,
                _key: s._key
              } : {
                ...sv,
                _key: s._key
              };
            });
          } else segments = r.segments;
          const scriptVersion = r.scriptVersion || st.data.sequence.scriptVersion;
          const sel = segments.some(x => x._key === st.sel) ? st.sel : segments[0] ? segments[0]._key : null;
          return {
            sel,
            data: {
              ...st.data,
              segments,
              sequence: {
                ...st.data.sequence,
                scriptVersion,
                estimatedSeconds: r.estimatedSeconds !== null ? r.estimatedSeconds : totalOf(segments)
              }
            },
            saved: this._dirtyAt > sentAt ? "Unsaved" : `Saved · v${scriptVersion}`
          };
        });
      } catch (e) {
        const info = api.errInfo(e);
        if (!this._alive) return;
        if (info.rendering) {
          this._pendingSave = true;
          this.setState({
            lock: true,
            saved: "Rendering: saved when it finishes"
          });
          this.attachBatch();
        } else if (info.soft) this.setState({
          saved: "Not saved: the storyboard route is not deployed yet"
        });else if (info.status === 422) {
          this.setState({
            saved: "Not saved"
          });
          toast(info.message || "A scene could not be saved.");
        } else {
          this.setState({
            saved: "Not saved"
          });
          toast(info.message || "Could not save the storyboard.");
        }
      } finally {
        this._saving = false;
        if (this._pendingSave && this._alive && !this.state.lock) {
          this._pendingSave = false;
          this.queueSave();
        }
      }
    });
    _defineProperty(this, "setSetting", (patch, debounced) => {
      const settings = {
        ...this.state.data.sequence.settings,
        ...patch
      };
      this.patchSeq({
        settings
      });
      this._pendingSettings.settings = settings;
      if (debounced) this.queueSettings();else this.flushSettings();
    });
    _defineProperty(this, "setSeq", (patch, debounced) => {
      this.patchSeq(patch);
      Object.assign(this._pendingSettings, patch);
      if (debounced) this.queueSettings();else this.flushSettings();
    });
    _defineProperty(this, "flushSettings", async () => {
      clearTimeout(this._settingsTimer);
      const body = this._pendingSettings;
      if (!Object.keys(body).length) return;
      if (this.state.lock) return; // re-sent by batchDone
      this._pendingSettings = {};
      try {
        const r = await api.updateSequence(this.seqId(), body);
        if (r && this._alive) this.patchSeq({
          scriptVersion: Math.max(r.sequence.scriptVersion, this.state.data.sequence.scriptVersion),
          brand: r.sequence.brand,
          brandKit: r.sequence.brandKit,
          status: r.sequence.status,
          title: r.sequence.title
        });
      } catch (e) {
        const info = api.errInfo(e);
        if (!this._alive) return;
        if (info.rendering) {
          Object.assign(this._pendingSettings, body);
          this.setState({
            lock: true
          });
          this.attachBatch();
          toast("Rendering: the settings are saved when it finishes.");
        } else if (info.soft) this.setState({
          saved: "Settings not saved: route not deployed yet"
        });else toast(info.message || "Could not save the settings.");
      }
    });
    _defineProperty(this, "toggleFormat", f => {
      const {
        formats
      } = this.state.data.sequence;
      if (formats.includes(f)) {
        if (formats.length === 1) {
          toast("Keep at least one size.");
          return;
        }
        const next = formats.filter(x => x !== f);
        this.setSeq({
          formats: next
        });
      } else this.setSeq({
        formats: api.FORMATS.map(([k]) => k).filter(k => k === f || formats.includes(k))
      });
    });
    /* ---------- agent actions ---------- */
    _defineProperty(this, "rewriteCut", async () => {
      this.stopPlay();
      this.setState({
        confirmCut: false,
        busy: "cut"
      });
      try {
        clearTimeout(this._saveTimer);
        const r = await api.firstCut(this.seqId(), {
          keepOrder: this.state.data.sequence.purpose === "tutorial"
        });
        if (!this._alive) return;
        this._dirtyAt = 0;
        this.setState(st => ({
          data: {
            ...st.data,
            sequence: {
              ...st.data.sequence,
              ...r.sequence,
              formats: r.sequence.formats.length ? r.sequence.formats : st.data.sequence.formats
            },
            segments: r.segments.length ? r.segments : st.data.segments
          },
          sel: r.segments[0] ? r.segments[0]._key : st.sel,
          saved: `Saved · v${r.sequence.scriptVersion}`,
          playT: 0
        }));
        toast(`New cut: ${r.segments.length} scenes.`);
      } catch (e) {
        const info = api.errInfo(e);
        toast(info.rendering ? "Rendering: the cut can be rewritten when it finishes." : info.soft ? "Rewrite the cut is not available on this server yet." : info.status === 422 ? "No assets to write from." : info.message || "Could not rewrite the cut.");
      } finally {
        if (this._alive) this.setState({
          busy: ""
        });
      }
    });
    _defineProperty(this, "rewriteLine", async (seg, instruction) => {
      if (!seg.id) return;
      this.setState({
        rewriting: seg._key
      });
      try {
        const r = await api.rewriteSegment(this.seqId(), seg.id, (instruction || "").trim() || undefined);
        if (!this._alive || !r) return;
        const now = this.segs().find(x => x._key === seg._key) || seg;
        this.patchSeg(seg._key, {
          narration: r.narration || now.narration,
          overlayText: r.overlayText || now.overlayText,
          narrationSeconds: r.narrationSeconds,
          estimatedSeconds: r.estimatedSeconds !== null ? r.estimatedSeconds : now.estimatedSeconds
        });
      } catch (e) {
        const info = api.errInfo(e);
        toast(info.rendering ? "Rendering: rewrite is available when it finishes." : info.soft ? "Rewrite is not available on this server yet." : info.message || "Could not rewrite this line.");
      } finally {
        if (this._alive) this.setState({
          rewriting: null
        });
      }
    });
    /* ---------- render ---------- */
    _defineProperty(this, "startRender", async formats => {
      const seq = this.state.data.sequence;
      const fm = formats && formats.length ? formats : seq.formats;
      this.stopPlay();
      clearTimeout(this._saveTimer);
      if (this._dirtyAt || this._saving) {
        await this.saveSegments();
        if (this._dirtyAt) {
          toast("Save the storyboard first (fix the scene that would not save).");
          return;
        }
      }
      if (Object.keys(this._pendingSettings).length) await this.flushSettings();
      this.setState({
        busy: "render",
        credits: null
      });
      try {
        const r = await api.startRender(this.seqId(), fm);
        if (!this._alive) return;
        api.saveBatch(this.seqId(), {
          batchId: r.batchId,
          renderIds: r.renders.map(x => x.id)
        });
        this.setState(st => ({
          data: {
            ...st.data,
            renders: [...r.renders.map(x => ({
              ...x,
              createdDate: x.createdDate || new Date().toISOString(),
              scriptVersion: x.scriptVersion || st.data.sequence.scriptVersion
            })), ...st.data.renders],
            sequence: {
              ...st.data.sequence,
              status: "rendering"
            }
          },
          sideTab: "exports",
          lock: true
        }), () => r.renders.forEach(x => this.pollOne(x.id, true)));
        toast(`Rendering ${fm.length} size${fm.length === 1 ? "" : "s"}. It takes a few minutes; you can leave this page.`);
      } catch (e) {
        const info = api.errInfo(e);
        if (!this._alive) return;
        if (info.insufficient) this.setState({
          credits: {
            insufficient: true,
            buyUrl: info.buyUrl
          }
        });else if (info.rendering) {
          toast("A render is already running for this sequence.");
          this.load();
        } else if (info.status === 422) {
          const ids = (info.data.segmentIds || []).map(api.idStr);
          if (ids.length) {
            const seg = this.segs().find(s => ids.includes(s.id));
            if (seg) this.setState({
              sel: seg._key,
              sideTab: "scene",
              watchId: null
            });
          }
          toast(info.code === "no_segments" ? "Nothing to render yet: add a scene." : info.code === "missing_media" ? `${ids.length || "Some"} scene${ids.length === 1 ? " has" : "s have"} no picture. Change or remove ${ids.length === 1 ? "it" : "them"} first.` : info.message || "Cannot render this storyboard yet.");
        } else if (info.soft) toast("Rendering is not available on this server yet.");else toast(info.message || "Could not start the render.");
      } finally {
        if (this._alive) this.setState({
          busy: ""
        });
      }
    });
    _defineProperty(this, "attach", async list => {
      const seq = this.state.data.sequence;
      if (!seq.containerId) return;
      this.setState({
        attachBusy: true
      });
      let n = 0;
      for (const r of list) {
        if (!r.assetId) continue;
        try {
          await api.linkAsset(r.assetId, {
            containerId: seq.containerId,
            role: "output"
          });
          n++;
        } catch (e) {/* one failure does not stop the rest */}
      }
      if (this._alive) this.setState({
        attachBusy: false
      });
      toast(n ? `${n} video${n === 1 ? "" : "s"} added to the piece. Ship them from the piece.` : "Could not add the videos to the piece.");
    });
    _defineProperty(this, "togglePlay", () => {
      if (this.state.playing) {
        this.stopPlay();
        return;
      }
      const total = totalOf(this.segs());
      if (!total) return;
      let start = this.state.playT;
      if (start >= total - 0.05) start = 0;
      this._t0 = performance.now() - start * 1000;
      this._lastIdx = -1;
      this.setState({
        playing: true,
        playT: start,
        watchId: null
      });
      clearInterval(this._tick);
      this._tick = setInterval(this.tick, TICK_MS);
      this.tick();
    });
    _defineProperty(this, "tick", () => {
      if (!this._alive) {
        clearInterval(this._tick);
        return;
      }
      const total = totalOf(this.segs());
      const t = (performance.now() - this._t0) / 1000;
      if (t >= total) {
        this.stopPlay(true);
        return;
      }
      const {
        i
      } = this.sceneAt(t);
      if (i !== this._lastIdx) {
        this._lastIdx = i;
        this.enterScene(i);
      }
      this.setState({
        playT: t
      });
    });
    _defineProperty(this, "stopPlay", ended => {
      clearInterval(this._tick);
      this._tick = null;
      if (this._voiceKey && playingKey() === this._voiceKey) stopAudio();
      this._voiceKey = null;
      if (!this._alive || !this.state.playing) return;
      if (ended === true) {
        const first = this.segs()[0];
        this.setState({
          playing: false,
          playT: 0,
          sel: first ? first._key : this.state.sel
        });
      } else this.setState({
        playing: false
      });
    });
    _defineProperty(this, "select", key => {
      if (this.state.playing) {
        const t = this.startOf(key);
        this._t0 = performance.now() - t * 1000;
        this._lastIdx = -1;
        this.setState({
          sel: key,
          playT: t,
          watchId: null
        });
        return;
      }
      this.setState({
        sel: key,
        playT: this.startOf(key),
        watchId: null,
        sideTab: this.state.sideTab === "exports" ? "scene" : this.state.sideTab
      });
    });
  }
  seqId() {
    return api.idStr(this.props.params && this.props.params.id);
  }
  componentDidMount() {
    this.load();
    document.addEventListener("keydown", this.onKey);
    window.addEventListener("beforeunload", this.onLeave);
  }
  componentWillUnmount() {
    this._alive = false;
    document.removeEventListener("keydown", this.onKey);
    window.removeEventListener("beforeunload", this.onLeave);
    this.stopPlay();
    clearTimeout(this._saveTimer);
    clearTimeout(this._settingsTimer);
    Object.values(this._polls).forEach(clearTimeout);
    if (this._dirtyAt && this.state.data) this.saveSegments(true);
    if (Object.keys(this._pendingSettings).length && this.state.data && !this.state.lock) this.flushSettings();
  }
  /* ---------- segments: edit, autosave ---------- */
  segs() {
    return this.state.data && this.state.data.segments || [];
  }
  setSegs(segments, cb) {
    this._dirtyAt = Date.now();
    this.setState(st => ({
      data: {
        ...st.data,
        segments
      },
      saved: "Unsaved"
    }), cb);
    this.queueSave();
  }
  segFromAsset(a, i) {
    const b = api.assetBucket(a);
    return {
      kind: a.kind === "video" ? b === "recording" ? "recording" : "clip" : "still",
      assetId: a.id,
      mediaUrl: a.url || null,
      thumbnailUrl: a.kind === "video" ? a.posterUrl || null : a.latestUrl || a.url || null,
      title: a.title || "",
      fit: b === "recording" ? "contain" : "auto",
      audioMode: b === "recording" ? "mute" : "duck",
      mediaSeconds: a.durationSeconds || null
    };
  }
  queueSave() {
    clearTimeout(this._saveTimer);
    this._saveTimer = setTimeout(() => this.saveSegments(), SAVE_MS);
  }
  /* ---------- settings (PUT /sequences/{id}) ---------- */
  patchSeq(patch) {
    this.setState(st => ({
      data: {
        ...st.data,
        sequence: {
          ...st.data.sequence,
          ...patch
        }
      }
    }));
  }
  queueSettings() {
    clearTimeout(this._settingsTimer);
    this._settingsTimer = setTimeout(() => this.flushSettings(), SETTINGS_MS);
  }
  /* ---------- Play all (a preview of the storyboard on its timings) ---------- */
  sceneAt(t) {
    const segs = this.segs();
    let acc = 0;
    for (let i = 0; i < segs.length; i++) {
      const l = lenOf(segs[i]);
      if (t < acc + l) return {
        i,
        start: acc
      };
      acc += l;
    }
    return {
      i: segs.length - 1,
      start: acc - (segs.length ? lenOf(segs[segs.length - 1]) : 0)
    };
  }
  startOf(key) {
    const segs = this.segs();
    let acc = 0;
    for (const s of segs) {
      if (s._key === key) return acc;
      acc += lenOf(s);
    }
    return 0;
  }
  /** Each scene as Play all reaches it: select it and, with voice on, play its line (cached; the next one is fetched ahead). */
  enterScene(i) {
    const segs = this.segs();
    const seg = segs[i];
    if (!seg) return;
    this.setState({
      sel: seg._key
    });
    if (this._voiceKey && playingKey() === this._voiceKey) stopAudio();
    this._voiceKey = null;
    if (!this.state.voiceOn) return;
    const go = this.preview(seg);
    if (go) go.then(p => {
      if (p && this.state.playing && this._lastIdx === i && this._alive) {
        this._voiceKey = p.key;
        playAudio(p.key, p.url);
      }
    });
    if (segs[i + 1]) this.preview(segs[i + 1]);
  }
  preview(seg) {
    if (!seg || isCard(seg)) return null;
    const text = String(seg.narration || "").replace(/\s+/g, " ").trim();
    if (!text || text.length > 300) return null;
    const voice = this.state.data.sequence.voice;
    const key = `play:${voice}:${text}`;
    if (!this._pv[key]) {
      this._pv[key] = previewLine(voice, text).then(r => {
        const url = r && (r.url || r.Url);
        return url ? {
          key,
          url
        } : null;
      }).catch(e => {
        delete this._pv[key];
        if (!this._pvWarned) {
          this._pvWarned = true;
          toast(`${e && e.message || "The voice preview is not available."} The preview keeps playing without it.`);
        }
        return null;
      });
    }
    return this._pv[key];
  }
  /* ---------- render ---------- */
  render() {
    const {
      data,
      loadErr
    } = this.state;
    const crumbs = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("a", {
      href: "#",
      onClick: e => {
        e.preventDefault();
        this.props.history.push("/Content");
      }
    }, "Content Hub"), " \u203A ", /*#__PURE__*/React.createElement("a", {
      href: "#",
      onClick: e => {
        e.preventDefault();
        this.props.history.push("/Content");
      }
    }, "Assets"), " \u203A Sequence");
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view sq-view sx-view"
    }, data === undefined ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: crumbs,
      title: "Sequence"
    }), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "Opening the storyboard\u2026")) : !data ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: crumbs,
      title: "Sequence"
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-empty"
    }, loadErr === "soft" ? "Sequences are not available on this server yet." : loadErr), /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.props.history.push("/Content")
    }, "Back to Assets"))) : this.renderEditor(), this.state.hook ? this.renderHook() : null, this.state.addOpen ? this.renderAdd() : null, this.state.changeOpen ? this.renderChange() : null);
  }
  renderHook() {
    const {
      kind,
      asset
    } = this.state.hook;
    const close = () => this.setState({
      hook: null
    });
    const saved = () => {
      this.load();
    };
    if (kind === "edit") return /*#__PURE__*/React.createElement(VideoEditModal, {
      asset: asset,
      onSaved: saved,
      onClose: close
    });
    if (kind === "trim") return /*#__PURE__*/React.createElement(TrimModal, {
      asset: asset,
      onSaved: saved,
      onClose: close
    });
    if (kind === "clips") return /*#__PURE__*/React.createElement(ClipsModal, {
      asset: asset,
      onSaved: saved,
      onClose: close
    });
    if (kind === "resize") return /*#__PURE__*/React.createElement(VideoResizeModal, {
      asset: asset,
      onSaved: saved,
      onClose: close
    });
    return null;
  }
  renderAdd() {
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-modal-backdrop hub-edit-backdrop",
      onClick: () => this.setState({
        addOpen: false
      })
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-trash-sheet hub-create sq-add",
      role: "dialog",
      "aria-label": "Add scenes from Assets",
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-create-head"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
      className: "hub-create-eyebrow"
    }, "Sequence"), /*#__PURE__*/React.createElement("h3", null, "Add scenes from Assets"), /*#__PURE__*/React.createElement("p", null, "Each pick becomes a scene at the end, in the order you pick. The voiceover stays empty until you write it or rewrite the cut.")), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle sm",
      style: {
        margin: 0
      },
      onClick: () => this.setState({
        addOpen: false
      })
    }, "Close")), /*#__PURE__*/React.createElement(AssetPicker, {
      picks: this.state.addPicks,
      onChange: addPicks => this.setState({
        addPicks
      }),
      onAssets: list => {
        this._addAssets = list || [];
      },
      max: api.MAX_PICKS - this.segs().length,
      compact: true
    }), /*#__PURE__*/React.createElement("div", {
      className: "hub-create-foot right"
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0,
        flex: 1
      }
    }, this.state.addPicks.length ? `${this.state.addPicks.length} picked` : "Pick one or more"), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !this.state.addPicks.length,
      onClick: this.addFromAssets
    }, "Add ", this.state.addPicks.length || "", " scene", this.state.addPicks.length === 1 ? "" : "s"))));
  }
  renderChange() {
    const seg = this.segs().find(x => x._key === this.state.sel);
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-modal-backdrop hub-edit-backdrop",
      onClick: () => this.setState({
        changeOpen: false
      })
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-trash-sheet hub-create sq-add",
      role: "dialog",
      "aria-label": "Change the picture of this scene",
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-create-head"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
      className: "hub-create-eyebrow"
    }, "Scene ", seg ? this.segs().indexOf(seg) + 1 : ""), /*#__PURE__*/React.createElement("h3", null, "Change the picture"), /*#__PURE__*/React.createElement("p", null, "Pick one photo or video. The voiceover, words on screen, transition and length stay as they are.")), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle sm",
      style: {
        margin: 0
      },
      onClick: () => this.setState({
        changeOpen: false
      })
    }, "Close")), /*#__PURE__*/React.createElement(AssetPicker, {
      single: true,
      accept: "any",
      picks: this.state.changePick,
      onChange: changePick => this.setState({
        changePick
      }),
      onAssets: list => {
        this._changeAssets = list || [];
      },
      compact: true
    }), /*#__PURE__*/React.createElement("div", {
      className: "hub-create-foot right"
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0,
        flex: 1
      }
    }, this.state.changePick.length ? "1 picked" : "Pick one"), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !this.state.changePick.length,
      onClick: this.changeAsset
    }, "Use this"))));
  }
  renderEditor() {
    const s = this.state;
    const {
      sequence: seq,
      segments,
      renders
    } = s.data;
    const lock = s.lock;
    const busy = !!s.busy;
    const agentOff = lock || busy;
    const sel = segments.find(x => x._key === s.sel) || segments[0] || null;
    const selIdx = sel ? segments.indexOf(sel) : -1;
    const lens = segments.map(lenOf);
    const sum = lens.reduce((t, l) => t + l, 0);
    const total = seq.estimatedSeconds && !this._dirtyAt ? seq.estimatedSeconds : segments.reduce((t, x) => t + segSeconds(x), 0);
    const captions = seq.settings.captions !== false && seq.settings.captionStyle !== "off";
    const brand = seq.brandKit ? seq.brand : null;
    const batch = latestBatch(renders);
    const inflight = batch.some(api.inFlight);
    const doneN = batch.filter(r => r.status === "done").length;
    const n = seq.formats.length;
    const watch = s.watchId ? (renders || []).find(r => r.id === s.watchId && r.status === "done" && r.url) : null;
    const savedOk = /^Saved/.test(s.saved || "");
    const minScene = Math.max(0.5, Number(seq.settings.minSceneSeconds) || 1);
    const stageSeg = sel;
    const stageIdx = selIdx;
    return /*#__PURE__*/React.createElement("div", {
      className: "sx"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sx-top"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sx-iconbtn",
      "aria-label": "Back to Assets",
      title: "Back to Assets",
      onClick: () => this.props.history.push("/Content")
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "chevron_left")), /*#__PURE__*/React.createElement("div", {
      className: "sx-title"
    }, s.titleEdit !== null ? /*#__PURE__*/React.createElement("input", {
      className: "sx-title-edit",
      autoFocus: true,
      "aria-label": "Sequence title",
      value: s.titleEdit,
      disabled: busy,
      onChange: e => this.setState({
        titleEdit: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Enter") e.target.blur();
        if (e.key === "Escape") this.setState({
          titleEdit: null
        });
      },
      onBlur: () => {
        const t = (s.titleEdit || "").trim();
        this.setState({
          titleEdit: null
        });
        if (t && t !== seq.title) this.setSeq({
          title: t
        });
      }
    }) : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sx-name",
      title: "Rename",
      disabled: busy,
      onClick: () => this.setState({
        titleEdit: seq.title
      })
    }, seq.title, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "edit")), /*#__PURE__*/React.createElement("span", null, api.purposeLabel(seq.purpose), " \xB7 ", segments.length, " scene", segments.length === 1 ? "" : "s", " \xB7 ", api.fmtClock(total), seq.containerId ? " · on a piece" : "")), /*#__PURE__*/React.createElement("span", {
      className: "sx-seg",
      role: "group",
      "aria-label": "Preview size"
    }, SIZES.map(([k, name, ratio]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      className: s.format === k ? "on" : "",
      "aria-pressed": s.format === k,
      title: seq.formats.includes(k) ? `Preview in ${name} ${ratio}` : `Preview in ${name} ${ratio} (not in the sizes to render)`,
      onClick: () => this.setState({
        format: k
      })
    }, name, " ", ratio))), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), inflight ? /*#__PURE__*/React.createElement("span", {
      className: "sx-progress"
    }, /*#__PURE__*/React.createElement("i", null), "Rendering ", Math.min(doneN + 1, batch.length), " of ", batch.length, " size", batch.length === 1 ? "" : "s") : /*#__PURE__*/React.createElement("span", {
      className: "sx-saved" + (savedOk ? "" : " dim"),
      title: s.saved
    }, savedOk ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "check"), "Saved") : s.saved), s.confirmCut ? /*#__PURE__*/React.createElement("span", {
      className: "sx-confirm"
    }, /*#__PURE__*/React.createElement("span", null, "Replace every scene with a new cut?"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sx-btn danger",
      onClick: this.rewriteCut
    }, "Rewrite"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sx-btn",
      onClick: () => this.setState({
        confirmCut: false
      })
    }, "Keep")) : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sx-btn",
      disabled: agentOff,
      title: lock ? "Available when the render finishes" : "A new first cut from the brief and the pieces. Every scene is replaced; nothing renders.",
      onClick: () => this.setState({
        confirmCut: true
      })
    }, s.busy === "cut" ? "Writing…" : "Rewrite the cut"), (() => {
      // Open in Studio (2026-10-11): the newest finished output video, when the workspace has both apps
      const out = hasBothApps() ? (renders || []).filter(r => r.status === "done" && r.url && r.assetId).sort((x, y) => String(y.finishedDate || y.createdDate || "").localeCompare(String(x.finishedDate || x.createdDate || "")))[0] : null;
      return out ? /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "sx-btn",
        title: "Open the newest finished video in Plunk Studio",
        onClick: () => window.open(`${studioUrl()}/?asset=${encodeURIComponent(String(out.assetId))}&back=${encodeURIComponent(`/Content/sequences/${this.seqId()}`)}`, "_blank", "noopener")
      }, "Open in Studio") : null;
    })(), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: agentOff || !segments.length,
      title: lock ? "A render is running" : `Render ${seq.formats.map(exportName).join(", ")}`,
      onClick: () => this.startRender()
    }, s.busy === "render" ? "Starting…" : lock ? "Rendering…" : `Render ${n} size${n === 1 ? "" : "s"}`)), lock ? /*#__PURE__*/React.createElement("div", {
      className: "sx-strip info",
      role: "status"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "hourglass_top"), /*#__PURE__*/React.createElement("span", null, "Rendering. You can keep editing: changes are saved when it finishes and show up on the next Render."), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sx-btn sm",
      onClick: () => this.setState({
        sideTab: "exports"
      })
    }, "See progress")) : null, s.credits && s.credits.insufficient ? /*#__PURE__*/React.createElement("div", {
      className: "sx-strip warn",
      role: "status"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "info"), /*#__PURE__*/React.createElement("span", null, "Not enough credits to render this sequence."), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      size: "small",
      onClick: () => window.open(s.credits.buyUrl || api.BUY_URL, "_blank", "noopener")
    }, "Buy credits"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sx-btn sm",
      onClick: () => this.setState({
        credits: null
      })
    }, "Dismiss")) : null, /*#__PURE__*/React.createElement("div", {
      className: "sx-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sx-left"
    }, watch ? /*#__PURE__*/React.createElement("div", {
      className: "sx-stage"
    }, /*#__PURE__*/React.createElement(ExportStage, {
      render: watch,
      name: exportName(watch.format),
      stale: seq.scriptVersion && watch.scriptVersion && watch.scriptVersion < seq.scriptVersion,
      onClose: () => this.setState({
        watchId: null
      })
    })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "sx-stage"
    }, /*#__PURE__*/React.createElement(SceneFrame, {
      seg: stageSeg,
      idx: stageIdx,
      format: s.format,
      captions: captions,
      brand: brand,
      len: stageSeg ? lenOf(stageSeg) : 0,
      playing: s.playing
    }), /*#__PURE__*/React.createElement("span", {
      className: "sx-honest"
    }, "Preview of the storyboard. The render adds camera moves, smooth transitions and music.")), /*#__PURE__*/React.createElement("div", {
      className: "sx-tlwrap"
    }, /*#__PURE__*/React.createElement(SequenceTimeline, {
      segs: segments,
      lens: lens,
      selKey: sel ? sel._key : null,
      brand: brand,
      playing: s.playing,
      playT: s.playT,
      total: sum,
      onPlay: this.togglePlay,
      voiceOn: s.voiceOn,
      onVoiceOn: voiceOn => {
        this.setState({
          voiceOn
        });
        if (!voiceOn && this._voiceKey) stopAudio();
      },
      canVoice: segments.some(x => !isCard(x) && x.narration),
      musicUrl: seq.musicUrl,
      editable: !busy,
      minScene: minScene,
      onSelect: this.select,
      onMove: this.moveSeg,
      onLength: this.setLength,
      onAdd: this.onAdd,
      addDisabled: busy || segments.length >= api.MAX_PICKS
    })))), this.renderSide(sel, selIdx, segments, lens, minScene, captions, brand)));
  }
  renderSide(sel, selIdx, segments, lens, minScene, captions, brand) {
    const s = this.state;
    const seq = s.data.sequence;
    const renders = s.data.renders;
    const busy = !!s.busy;
    const tab = s.sideTab;
    const batch = latestBatch(renders);
    const inflight = batch.some(api.inFlight);
    const ready = latestDonePerFormat(renders, seq.formats).filter(Boolean).length;
    return /*#__PURE__*/React.createElement("div", {
      className: "sx-side"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sx-tabs",
      role: "tablist",
      "aria-label": "Editor panels"
    }, [["scene", "Scene"], ["sound", "Sound"], ["look", "Look"], ["exports", "Exports"]].map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      role: "tab",
      "aria-selected": tab === k,
      className: tab === k ? "on" : "",
      onClick: () => this.setState({
        sideTab: k
      })
    }, l, k === "exports" && inflight ? /*#__PURE__*/React.createElement("span", {
      className: "dot",
      "aria-label": "rendering"
    }) : k === "exports" && ready ? /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, ready) : null))), /*#__PURE__*/React.createElement("div", {
      className: "sx-pane",
      role: "tabpanel"
    }, tab === "scene" ? /*#__PURE__*/React.createElement(ScenePanel, {
      seg: sel,
      i: selIdx,
      n: segments.length,
      next: selIdx >= 0 ? segments[selIdx + 1] : null,
      voice: seq.voice,
      brand: brand,
      disabled: busy,
      canAgent: !s.lock && !busy,
      rewriting: sel ? s.rewriting === sel._key : false,
      len: selIdx >= 0 ? lens[selIdx] : 0,
      minScene: minScene,
      onPatch: this.patchSeg,
      onPatchNext: this.patchNext,
      onRewrite: this.rewriteLine,
      onChangeAsset: () => this.setState({
        changeOpen: true,
        changePick: sel && sel.assetId ? [sel.assetId] : []
      }),
      onDuplicate: () => sel && this.duplicateSeg(sel._key),
      onRemove: this.removeSeg,
      onMove: (from, to) => {
        this.moveSeg(from, to);
      }
    }) : tab === "sound" ? /*#__PURE__*/React.createElement(SoundPanel, {
      seq: seq,
      disabled: busy,
      captions: captions,
      onSeq: this.setSeq,
      onSetting: this.setSetting
    }) : tab === "look" ? /*#__PURE__*/React.createElement(LookPanel, {
      seq: seq,
      disabled: busy,
      onSeq: this.setSeq,
      onSetting: this.setSetting,
      onToggleFormat: this.toggleFormat
    }) : /*#__PURE__*/React.createElement(ExportsPanel, {
      sequence: seq,
      renders: renders,
      busy: busy,
      attachBusy: s.attachBusy,
      onRetry: f => this.startRender(f),
      onAttach: this.attach,
      onHook: (kind, asset) => this.setState({
        hook: {
          kind,
          asset
        }
      }),
      watchId: s.watchId,
      onWatch: r => {
        this.stopPlay();
        this.setState({
          watchId: r ? r.id : null
        });
      }
    })));
  }
}
export const SequenceWorkspaceViewComponent = withRouter(SequenceWorkspace);
export default SequenceWorkspaceViewComponent;